import { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Button from '../../../components/bootstrap/Button';
import Spinner from '../../../components/bootstrap/Spinner';
import { toast } from 'react-toastify';
import useDarkMode from '../../../hooks/useDarkMode';
import CustomTextInput from '../CustomTextInput';
import authAPI from '../../API/authAPI';
import Icon from '../../../components/icon/Icon';

interface VerifyOtpComponentProps {
	email: string;
	onBack: () => void;
	onVerified: () => void;
}

const VerifyOtpComponent = ({ email, onBack, onVerified }: VerifyOtpComponentProps) => {
	const { darkModeStatus } = useDarkMode();
	const [isLoading, setIsLoading] = useState(false);
	const [isResending, setIsResending] = useState(false);
	const [timeLeft, setTimeLeft] = useState(900); // 15 minutes in seconds
	const [canResend, setCanResend] = useState(false);

	useEffect(() => {
		if (timeLeft <= 0) {
			setCanResend(true);
			return;
		}

		const timer = setTimeout(() => {
			setTimeLeft(timeLeft - 1);
		}, 1000);

		return () => clearTimeout(timer);
	}, [timeLeft]);

	const formatTime = (seconds: number) => {
		const minutes = Math.floor(seconds / 60);
		const remainingSeconds = seconds % 60;
		return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
	};

	const formik = useFormik({
		initialValues: {
			code: '',
		},
		validationSchema: Yup.object().shape({
			code: Yup.string()
				.required('Verification code is required')
				.matches(/^\d{6}$/, 'Code must be 6 digits'),
		}),
		onSubmit: async (values) => {
			setIsLoading(true);
			try {
				const data = await authAPI.verifyOtp(email, values.code);
				if (data.success) {
					toast.success(data.message || 'Code verified successfully', {
						position: 'top-right',
						autoClose: 3000,
						theme: darkModeStatus ? 'dark' : 'light',
					});
					onVerified();
				} else {
					toast.error(data.message || 'Invalid verification code', {
						position: 'top-right',
						autoClose: 5000,
						theme: darkModeStatus ? 'dark' : 'light',
					});
				}
			} catch (error: any) {
				const errorMessage = error?.response?.data?.message || error?.message || 'Invalid or expired code. Please try again.';
				toast.error(errorMessage, {
					position: 'top-right',
					autoClose: 5000,
					theme: darkModeStatus ? 'dark' : 'light',
				});
			} finally {
				setIsLoading(false);
			}
		},
	});

	const handleResendCode = async () => {
		setIsResending(true);
		try {
			const data = await authAPI.forgotPassword(email);
			if (data.success) {
				toast.success('New verification code sent to your email', {
					position: 'top-right',
					autoClose: 3000,
					theme: darkModeStatus ? 'dark' : 'light',
				});
				formik.resetForm();
				setTimeLeft(900); // Reset timer
				setCanResend(false);
			} else {
				toast.error(data.message || 'Failed to resend code', {
					position: 'top-right',
					autoClose: 5000,
					theme: darkModeStatus ? 'dark' : 'light',
				});
			}
		} catch (error: any) {
			const errorMessage = error?.response?.data?.message || error?.message || 'Failed to resend code. Please try again.';
			toast.error(errorMessage, {
				position: 'top-right',
				autoClose: 5000,
				theme: darkModeStatus ? 'dark' : 'light',
			});
		} finally {
			setIsResending(false);
		}
	};

	return (
		<div className="verify-otp-form">
			<div className="text-center mb-4">
				<Icon icon="MarkEmailRead" size="3x" color="success" className="mb-3" />
				<h4 className="mb-2">Verify Your Code</h4>
				<p className="text-muted">
					We've sent a 6-digit verification code to <br />
					<strong>{email}</strong>
				</p>
				<div className="mb-2">
					{timeLeft > 0 ? (
						<small className={`text-${timeLeft > 300 ? 'success' : timeLeft > 60 ? 'warning' : 'danger'}`}>
							Code expires in {formatTime(timeLeft)}
						</small>
					) : (
						<small className="text-danger">
							Code has expired. Please request a new one.
						</small>
					)}
				</div>
			</div>

			<form onSubmit={formik.handleSubmit}>
				<div className="mb-4">
					<CustomTextInput
						id="code"
						placeholder="000000"
                        //@ts-ignore
						maxLength={6}
						value={formik.values.code}
						isTouched={formik.touched.code}
						invalidFeedback={formik.errors.code}
						isValid={formik.isValid}
						onChange={(e: any) => {
							const value = e.target.value.replace(/\D/g, '');
							formik.setFieldValue('code', value);
						}}
						onBlur={formik.handleBlur}
						className="text-center fs-4 fw-bold"
						style={{ letterSpacing: '0.5em', fontFamily: 'monospace' }}
					/>
				</div>

				<div className="d-grid gap-2 mb-3">
					<Button
						type="submit"
						color="success"
						className="py-3"
						isDisable={isLoading || !formik.isValid || !formik.values.code || timeLeft <= 0}
					>
						{isLoading && <Spinner className="me-2" isSmall inButton isGrow />}
						{timeLeft <= 0 ? 'Code Expired' : 'Verify Code'}
					</Button>
				</div>

				<div className="d-flex gap-2">
					<Button
						type="button"
						color="light"
						className="py-3 flex-fill"
						onClick={onBack}
						isDisable={isLoading || isResending}
					>
						<Icon icon="ArrowBack" className="me-2" />
						Back
					</Button>
					
					<Button
						type="button"
						color={canResend ? "info" : "light"}
						isLight={!canResend}
						className="py-3 flex-fill"
						onClick={handleResendCode}
						isDisable={isLoading || isResending || !canResend}
						title={!canResend ? "Wait for the timer to expire before resending" : "Send a new verification code"}
					>
						{isResending && <Spinner className="me-2" isSmall inButton isGrow />}
						{isResending ? 'Sending...' : canResend ? 'Resend Code' : `Wait ${formatTime(timeLeft)}`}
					</Button>
				</div>
			</form>
		</div>
	);
};

export default VerifyOtpComponent;